/* TI·FORGE Theme JS — resilient AJAX cart drawer and product media controls. */
(() => {
  const routes = () => window.exoRoutes || {};
  const route = path => `${routes().root || '/'}${path}`.replace(/([^:]\/)\/+/, '$1');

  const escapeHTML = value => String(value ?? '').replace(/[&<>'"]/g, char => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[char]));

  const imageURL = value => {
    if (!value) return '';
    return `${value}${value.includes('?') ? '&' : '?'}width=160`;
  };

  async function requestJSON(path, options = {}) {
    const response = await fetch(route(path), {
      ...options,
      headers: { Accept: 'application/json', ...(options.headers || {}) }
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'Request failed');
    return data;
  }

  function setCartButtonState(open) {
    document.querySelector('.cart-btn')?.setAttribute('aria-expanded', String(open));
    document.getElementById('cartDrawer')?.setAttribute('aria-hidden', String(!open));
  }

  window.CartDrawer = {
    open() {
      document.body.classList.add('cart-open');
      setCartButtonState(true);
      const drawer = document.getElementById('cartDrawer');
      this.returnFocus = document.activeElement;
      if (drawer) drawer.inert = false;
      drawer?.querySelector('[data-cart-close]')?.focus();
      this.render();
    },
    close() {
      document.body.classList.remove('cart-open');
      setCartButtonState(false);
      document.getElementById('cartDrawer')?.setAttribute('inert', '');
      this.returnFocus?.focus();
    },
    money(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency', currency: routes().currency || 'USD'
        }).format(Number(cents || 0) / 100);
      } catch {
        return String(cents || 0);
      }
    },
    async add(variantId, quantity = 1, sellingPlan = '') {
      if (!variantId) throw new Error('Missing product variant');
      const item = { id: Number(variantId), quantity: Math.max(1, Number(quantity) || 1) };
      if (sellingPlan) item.selling_plan = sellingPlan;
      await requestJSON('cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] })
      });
      this.open();
    },
    async change(key, quantity) {
      await requestJSON('cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: Math.max(0, Number(quantity) || 0) })
      });
      await this.render();
    },
    async render() {
      const body = document.getElementById('cartBody');
      const foot = document.getElementById('cartFoot');
      if (!body || !foot) return;

      try {
        const cart = await requestJSON('cart.js');
        const count = document.getElementById('cartCount');
        if (count) count.textContent = cart.item_count;

        if (cart.item_count === 0) {
          body.innerHTML = `<div class="cart-empty"><p>YOUR CART IS EMPTY</p>
            <a href="${escapeHTML(routes().allProducts || '/collections/all')}" data-cart-close style="text-decoration:underline;text-underline-offset:4px">CONTINUE SHOPPING</a></div>`;
          foot.style.display = 'none';
          return;
        }

        body.innerHTML = cart.items.map(item => {
          const key = escapeHTML(item.key);
          const title = escapeHTML(item.product_title);
          const variant = item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="variant">${escapeHTML(item.variant_title)}</p>` : '';
          const image = item.image ? `<img src="${escapeHTML(imageURL(item.image))}" alt="${title}" loading="lazy" width="80" height="80">` : '';
          return `<div class="cart-item">
            ${image}
            <div class="info">
              <h4>${title}</h4>
              ${variant}
              <div class="qty">
                <button type="button" data-cart-change data-cart-key="${key}" data-cart-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button>
                <span>${item.quantity}</span>
                <button type="button" data-cart-change data-cart-key="${key}" data-cart-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
              </div>
              <button type="button" class="remove" data-cart-change data-cart-key="${key}" data-cart-quantity="0">REMOVE</button>
            </div>
            <p class="line-price">${this.money(item.final_line_price)}</p>
          </div>`;
        }).join('');
        const total = document.getElementById('cartTotal');
        if (total) total.textContent = this.money(cart.total_price);
        foot.style.display = 'block';
      } catch (error) {
        body.innerHTML = '<p class="cart-error">Unable to load your cart. Please try again.</p>';
        foot.style.display = 'none';
        console.error('[CartDrawer]', error);
      }
    }
  };

  function setFormBusy(form, busy) {
    form.toggleAttribute('aria-busy', busy);
    form.dataset.loading = String(busy);
    form.querySelectorAll('button[type="submit"]').forEach(button => {
      button.disabled = busy || button.dataset.wasDisabled === 'true';
    });
  }

  function showFormError(form, message) {
    let error = form.querySelector('[data-cart-error]');
    if (!error) {
      error = document.createElement('p');
      error.dataset.cartError = 'true';
      error.className = 'cart-error';
      error.setAttribute('role', 'alert');
      form.appendChild(error);
    }
    error.textContent = message;
  }

  document.addEventListener('submit', async event => {
    const form = event.target.closest('form[data-ajax-cart]');
    if (!form) return;
    event.preventDefault();
    if (form.dataset.loading === 'true') return;
    form.querySelector('[data-cart-error]')?.remove();
    const data = new FormData(form);
    const submit = form.querySelector('button[type="submit"]');
    if (submit) submit.dataset.wasDisabled = String(submit.disabled);
    setFormBusy(form, true);
    try {
      await window.CartDrawer.add(data.get('id'), data.get('quantity'), data.get('selling_plan'));
    } catch (error) {
      showFormError(form, error.message || 'Unable to add this item.');
    } finally {
      setFormBusy(form, false);
    }
  });

  document.addEventListener('click', event => {
    const menuButton = event.target.closest('[data-menu-toggle]');
    if (menuButton) {
      const open = document.body.classList.toggle('menu-open');
      menuButton.setAttribute('aria-expanded', String(open));
      return;
    }
    if (event.target.closest('[data-cart-open]')) {
      window.CartDrawer.open();
      return;
    }
    const change = event.target.closest('[data-cart-change]');
    if (change) {
      const body = document.getElementById('cartBody');
      if (body?.getAttribute('aria-busy') === 'true') return;
      body?.setAttribute('aria-busy', 'true');
      body?.querySelectorAll('[data-cart-change]').forEach(button => button.disabled = true);
      window.CartDrawer.change(change.dataset.cartKey, change.dataset.cartQuantity)
        .catch(error => {
          const note = document.createElement('p');
          note.className = 'cart-error'; note.setAttribute('role', 'alert');
          note.textContent = error.message || 'Unable to update quantity.';
          body?.appendChild(note);
        }).finally(() => {
          body?.removeAttribute('aria-busy');
          body?.querySelectorAll('[data-cart-change]').forEach(button => button.disabled = false);
        });
      return;
    }
    if (event.target.closest('[data-cart-close]')) window.CartDrawer.close();

    const quantityButton = event.target.closest('[data-quantity-increment], [data-quantity-decrement]');
    if (quantityButton) {
      const input = quantityButton.parentElement?.querySelector('input[type="number"]');
      if (!input) return;
      const min = Number(input.min || 1);
      const max = Number(input.max || Infinity);
      const step = Number(input.step || 1);
      const direction = quantityButton.hasAttribute('data-quantity-increment') ? 1 : -1;
      input.value = Math.min(max, Math.max(min, Number(input.value || min) + direction * step));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  document.addEventListener('keydown', event => {
    if (!document.body.classList.contains('cart-open')) return;
    if (event.key === 'Escape') window.CartDrawer.close();
    if (event.key === 'Tab') {
      const drawer = document.getElementById('cartDrawer');
      const nodes = [...drawer.querySelectorAll('button:not([disabled]),a[href],input:not([disabled])')].filter(el => el.getClientRects().length);
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (!first) return;
      if (event.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) { event.preventDefault();last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) { event.preventDefault();first.focus(); }
    }
  });

  /* Backward-compatible helpers retained for existing theme-editor snippets. */
  window.filterProducts = (button, type) => {
    document.querySelectorAll('.filter-btns button').forEach(item => item.classList.remove('active'));
    button?.classList.add('active');
    document.querySelectorAll('.product-grid .card').forEach(card => {
      card.style.display = (type === 'All' || card.dataset.type === type) ? '' : 'none';
    });
  };

  window.pdpAdd = async form => {
    const data = new FormData(form);
    await window.CartDrawer.add(data.get('id'), data.get('quantity'), data.get('selling_plan'));
    return false;
  };

  function fitMediaFrame(media, box) {
    const update = () => {
      const width = media.naturalWidth || media.videoWidth;
      const height = media.naturalHeight || media.videoHeight;
      if (width && height) box.style.setProperty('--exo-media-ratio', width / height);
    };
    media.addEventListener('load', update, { once: true });
    media.addEventListener('loadedmetadata', update, { once: true });
    update();
  }

  function cornerNodes() {
    return ['tl', 'tr', 'bl', 'br'].map(position => {
      const node = document.createElement('span');
      node.className = `hud-corner ${position}`;
      return node;
    });
  }

  const initialMedia = document.querySelector('#mainMediaBox > img, #mainMediaBox > video');
  if (initialMedia) fitMediaFrame(initialMedia, document.getElementById('mainMediaBox'));

  document.addEventListener('click', event => {
    const thumb = event.target.closest('[data-media-switch]');
    if (!thumb) return;
    const box = document.getElementById('mainMediaBox');
    if (!box) return;
    document.querySelectorAll('[data-media-switch]').forEach(item => {
      const active = item === thumb;
      item.classList.toggle('active', active);
      item.setAttribute('aria-pressed', String(active));
    });

    if (thumb.dataset.mediaType === 'video') {
      const template = document.getElementById(`pdpVideo_${thumb.dataset.mediaId}`);
      const video = template?.content.querySelector('video')?.cloneNode(true);
      if (!video) return;
      box.replaceChildren(...cornerNodes(), video);
      fitMediaFrame(video, box);
    } else {
      const image = new Image();
      image.alt = thumb.querySelector('img')?.alt || '';
      image.src = thumb.dataset.mediaUrl || '';
      image.style.cssText = 'width:100%;height:auto;display:block;object-fit:contain;';
      box.replaceChildren(...cornerNodes(), image);
      fitMediaFrame(image, box);
    }
  });
})();
